'use client';

import { c as _c } from "react/compiler-runtime";
import type { Fetchable } from '@morpho-org/blue-sdk';
import type { Hex } from 'viem';
import { isDefined } from '@morpho-org/morpho-ts';
import { toast, Toaster } from '@repo/ui';
import { createContext, type PropsWithChildren, type ReactNode, useContext } from 'react';
import ExplorerUrl from '../../components/client/Toast/ExplorerUrl/ExplorerUrl';
import { NotificationStatus } from './Toast.types';
type ToastId = string | number;
interface ToastContextProps {
  displayTxToast: (args: DisplayTxToastArgs) => ToastId | undefined;
  displayToast: (args: NotificationBase) => ToastId;
  closeToasts: (ids: ToastId[]) => void;
}
export interface NotificationBase {
  description: ReactNode;
  notificationStatus: NotificationStatus;
  duration?: number;
  dedupeKey?: string;
}
export interface DisplayTxToastArgs extends Omit<NotificationBase, 'description'> {
  txHash: Fetchable<Hex>;
  chainId: Fetchable<number>;
  userIsContract: Fetchable<boolean>;
}
const DEFAULT_DURATION = 8000;
const toastByStatus = {
  [NotificationStatus.success]: toast.success,
  [NotificationStatus.error]: toast.error,
  [NotificationStatus.pending]: toast.warning,
  [NotificationStatus.signing]: toast.info
} satisfies Record<NotificationStatus, typeof toast.success>;
let nextId = 0;
const idByDedupeKey = new Map<string, ToastId>();

/**
 * Returns a fresh toast id and dismisses the last toast shown under `dedupeKey`.
 * Sonner keeps an updated toast's remaining time, so a reused id would close early.
 */
export const claimToastId = (dedupeKey?: string): ToastId => {
  const id = `vaults-toast-${nextId++}`;
  if (dedupeKey) {
    const previous = idByDedupeKey.get(dedupeKey);
    if (previous !== undefined) toast.dismiss(previous);
    idByDedupeKey.set(dedupeKey, id);
  }
  return id;
};
const displayToast = ({
  description,
  notificationStatus,
  duration,
  dedupeKey
}: NotificationBase) => toastByStatus[notificationStatus](description, {
  id: claimToastId(dedupeKey),
  duration
});
const ToastTxDescription = t0 => {
  const $ = _c(6);
  const {
    txHash,
    chainId,
    notificationStatus
  } = t0;
  switch (notificationStatus) {
    case NotificationStatus.success:
      {
        let t1;
        if ($[0] !== chainId || $[1] !== txHash) {
          t1 = <div>Your transaction <ExplorerUrl txHash={txHash} chainId={chainId} /> has been successfully executed!</div>;
          $[0] = chainId;
          $[1] = txHash;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    case NotificationStatus.error:
      {
        let t1;
        if ($[3] !== chainId || $[4] !== txHash) {
          t1 = <div>Your transaction <ExplorerUrl txHash={txHash} chainId={chainId} /> has not been executed. Try again.</div>;
          $[3] = chainId;
          $[4] = txHash;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    default:
      {
        return null;
      }
  }
};
const displayTxToast = ({
  notificationStatus,
  txHash,
  duration,
  chainId,
  userIsContract,
  dedupeKey
}: DisplayTxToastArgs) => {
  if (userIsContract) {
    return displayToast({
      notificationStatus: NotificationStatus.success,
      description: 'Your transaction has been successfully sent to your connected wallet.',
      dedupeKey
    });
  }
  if (!isDefined(txHash) || !isDefined(chainId)) return;
  return displayToast({
    notificationStatus,
    description: <ToastTxDescription txHash={txHash} chainId={chainId} notificationStatus={notificationStatus} />,
    duration,
    dedupeKey
  });
};
const closeToasts = (ids: ToastId[]) => {
  ids.forEach(id => toast.dismiss(id));
};
const toastFacade: ToastContextProps = {
  displayTxToast,
  displayToast,
  closeToasts
};
const ToastContext = createContext<ToastContextProps>(toastFacade);
const ToastProvider = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Toaster position="top-right" duration={DEFAULT_DURATION} closeButton={true} expand={true} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <ToastContext.Provider value={toastFacade}>{children}{t1}</ToastContext.Provider>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export const useToastContext = () => {
  return useContext(ToastContext);
};
export default ToastProvider;